import { type FacePackerPolygon } from '@payaca/components/solarRoofView/SolarRoofView';

type LatLngPoint = { lat: number; lng: number };

/** Two welded vertices: the same physical point on two faces' outer rings. */
export type FaceVertexLink = {
  a: { faceId: number; vertexIndex: number };
  b: { faceId: number; vertexIndex: number };
};

const EPSILON_DEG = 1e-9;

const pointsEqual = (p: LatLngPoint, q: LatLngPoint): boolean =>
  Math.abs(p.lat - q.lat) < EPSILON_DEG &&
  Math.abs(p.lng - q.lng) < EPSILON_DEG;

export type BoundaryChange =
  | { kind: 'translation'; dLat: number; dLng: number }
  | {
      kind: 'reshape';
      moved: { vertexIndex: number; dLat: number; dLng: number }[];
    }
  /** Vertex count changed — links are no longer meaningful. */
  | { kind: 'topology' };

export const classifyBoundaryChange = (
  oldOuter: LatLngPoint[],
  newOuter: LatLngPoint[]
): BoundaryChange => {
  if (oldOuter.length !== newOuter.length) return { kind: 'topology' };
  const moved: { vertexIndex: number; dLat: number; dLng: number }[] = [];
  for (let i = 0; i < oldOuter.length; i++) {
    if (!pointsEqual(oldOuter[i], newOuter[i])) {
      moved.push({
        vertexIndex: i,
        dLat: newOuter[i].lat - oldOuter[i].lat,
        dLng: newOuter[i].lng - oldOuter[i].lng,
      });
    }
  }
  if (moved.length === oldOuter.length && moved.length > 0) {
    const { dLat, dLng } = moved[0];
    const uniform = moved.every(
      (m) =>
        Math.abs(m.dLat - dLat) < EPSILON_DEG &&
        Math.abs(m.dLng - dLng) < EPSILON_DEG
    );
    if (uniform) return { kind: 'translation', dLat, dLng };
  }
  return { kind: 'reshape', moved };
};

/** Returns only the OTHER faces whose polygons changed — not the edited face. */
export const propagateReshape = (args: {
  editedFaceId: number;
  moved: { vertexIndex: number; dLat: number; dLng: number }[];
  links: FaceVertexLink[];
  polygonsByFaceId: Map<number, FacePackerPolygon>;
}): Map<number, FacePackerPolygon> => {
  const { editedFaceId, moved, links, polygonsByFaceId } = args;
  const changed = new Map<number, FacePackerPolygon>();

  for (const move of moved) {
    for (const link of links) {
      let partner: { faceId: number; vertexIndex: number } | null = null;
      if (
        link.a.faceId === editedFaceId &&
        link.a.vertexIndex === move.vertexIndex
      ) {
        partner = link.b;
      } else if (
        link.b.faceId === editedFaceId &&
        link.b.vertexIndex === move.vertexIndex
      ) {
        partner = link.a;
      }
      if (!partner || partner.faceId === editedFaceId) continue;

      const current =
        changed.get(partner.faceId) ?? polygonsByFaceId.get(partner.faceId);
      if (!current || partner.vertexIndex >= current.outer.length) continue;
      const outer = current.outer.map((p, i) =>
        i === partner.vertexIndex
          ? { lat: p.lat + move.dLat, lng: p.lng + move.dLng }
          : p
      );
      changed.set(partner.faceId, { outer, holes: current.holes });
    }
  }
  return changed;
};

export const dropLinksForFace = (
  links: FaceVertexLink[],
  faceId: number
): FaceVertexLink[] =>
  links.filter((l) => l.a.faceId !== faceId && l.b.faceId !== faceId);
